import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Owns the modular "how do you want to fill this form?" choice.
 *
 *   - chooseMode('manual' | 'ai'): PATCH /jobs/<id>/ to persist the pick, then
 *     update local job state. Detection is NOT auto-run — in AI mode the user
 *     clicks "Detect Fields" first.
 *   - runDetect(): POST /jobs/<id>/detect/ (on-demand AI annotation) and poll
 *     until the job leaves "extracting_fields".
 *
 * `mode` is read from the job (job.mode); this hook only drives transitions.
 */
export default function useAutoPdfMode({
  jobId,
  setJob,
  pollJobUntilDetected,
}) {
  const [detecting, setDetecting] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);

  const runDetect = useCallback(async () => {
    if (detecting) return;
    try {
      setDetecting(true);
      const res = await api.post(`/api/v1/rfp/autopdf/jobs/${jobId}/detect/`);
      if (![200, 201, 202].includes(res.status)) {
        throw new Error("Detect request failed");
      }
      // detect runs synchronously today but may go async — poll to be safe.
      await pollJobUntilDetected();
      toast.success("AI annotated the form. Review the fields, then Generate Answers.");
    } catch (err) {
      console.error("AI detect failed:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "AI annotation failed.";
      toast.error(msg);
    } finally {
      setDetecting(false);
    }
  }, [detecting, jobId, pollJobUntilDetected]);

  const chooseMode = useCallback(
    async (mode) => {
      if (switchingMode) return;
      try {
        setSwitchingMode(true);
        const res = await api.patch(`/api/v1/rfp/autopdf/jobs/${jobId}/`, { mode });
        if (![200, 201].includes(res.status)) {
          throw new Error("Failed to set mode");
        }
        setJob((prev) => (prev ? { ...prev, mode } : prev));
        // Detection is user-driven (the "Detect Fields" button), not automatic.
      } catch (err) {
        console.error("Set mode failed:", err);
        toast.error(err.response?.data?.error || err.message || "Failed to set mode.");
      } finally {
        setSwitchingMode(false);
      }
    },
    [switchingMode, jobId, setJob]
  );

  return { detecting, switchingMode, chooseMode, runDetect };
}
